<template>
  <div class="test-card" :class="{'delete-hover':isDeleteClicked, 'automated-test-card': isAutomated, 'repetitive-test-card': !isOneTime}">
    <div class="remove-test">
      <v-btn @click="deleteTestCard" icon><v-icon>close</v-icon></v-btn>
    </div>
    <div class="test-name">
      {{ $t(`test.testType.${testCard.type}`).toUpperCase() }}
      <div v-if='isPremium'>
        <diamond class='premium-feature'/>
      </div>
    </div>
    <div class="test-url-container">
      <div class="test-url" :title='testCard.url'>
        {{ testCard.url }}
      </div>
      <div v-if="authentication && authentication.hasKey" class="authentication-key-inline">
        <span class="material-icons-outlined key-icon">vpn_key</span>
        <span class="key-name">{{ authentication.keyName }}</span>
      </div>
    </div>
    <div class='section-wrapper'>
      <div class="test-time">
        <div class='test-time__date-row'>
          <span>{{ startDate }}</span>
        </div>
        <span>{{ testCard.frequency }}</span>
        <span>{{ testCard.startTime }}</span>
      </div>
      <div class='auto-remediation'>
        <div class='auto-remediation__label' v-if='isAutomated'>{{$t('test.automated.remediation')}}</div>
        <div class="status-with-id">
          <div class="card-project-id" v-if="publicProjectId" :title="`Project ${publicProjectId}`">#{{ publicProjectId }}</div>
          <div class="status" :class="projectStatus">{{ projectStatus }}</div>
        </div>
      </div>
    </div>

    <div class="test-control">
      <div class="test-control__icons">
        <custom-tooltip :content="`Turn ${ notificationEnabled ? 'off' : 'on' } alerts`">
          <div :class="['alerts', { notificationEnabled }]" slot="buttonContent">
            <notification-enabled-icon v-if="notificationEnabled" />
            <notification-disabled-icon v-else />
          </div>
        </custom-tooltip>
        <custom-tooltip v-if="testInitiated && projectId.length" :content="$t('test.card.open.project.tooltip')">
          <div :class="['open-project']" slot="buttonContent">
            <v-icon @click.stop='goToProject'>open_in_new</v-icon>
          </div>
        </custom-tooltip>
        <custom-tooltip v-if="canRetry" :content="$t('configuration.retry.scan.tooltip')">
          <div class="retry-scan" :class="{ 'retry-scan--loading': retryInProgress }" slot="buttonContent" @click.stop="retryScan">
            <v-progress-circular v-if="retryInProgress" indeterminate :size="16" :width="2" />
            <v-icon v-else>refresh</v-icon>
          </div>
        </custom-tooltip>
      </div>
      <div v-if="testInitiated && !canRetry" class="test-initiated">
        {{ $t('configuration.test.initiated') }}
      </div>
      <v-btn v-else-if="!canRetry" class="initiate-test-btn" @click="initiateTest">
        {{ $t('configuration.initiate.test') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import NotificationEnabledIcon from '@/assets/icons/bell_icn@1x.svg?inline';
import NotificationDisabledIcon from '@/assets/icons/bell_icn_2@1x.svg?inline';
import { DELETE_TEST_CARD, INITIATE_TEST_CARD, RETRY_SCAN } from '@/store/project/actionTypes';
import { ACTIVE_TEST_CARD_TYPES, projectStatuses } from '@/constants/projects';
import { formatStartDate } from '@/utils/dateUtils';
import { views } from '@/router/constants';
import Diamond from '@/assets/icons/diamond.svg?inline';
import CustomTooltip from '../common/CustomTooltip.vue';

export default {
  name: 'TestCard',
  components: {
    CustomTooltip,
    NotificationEnabledIcon,
    NotificationDisabledIcon,
    Diamond,
  },
  props: {
    testCard: {
      type: Object,
    },
    website: {},
    authentication: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      deleteClicked: false,
      retryInProgress: false,
    };
  },
  computed: {
    projectStatus() {
      return this.testCard?.project?.status;
    },
    isAutomated() {
      return this.testCard.metaData?.isAutomated || false;
    },
    isOneTime() {
      return this.testCard.frequency === 'never';
    },
    isPremium() {
      return this.testCard.type === 'cloud_compliance';
    },
    startDate() {
      return formatStartDate(this.testCard.startDate, true);
    },
    projectId() {
      if (this.testCard.project?.id) {
        return this.testCard.project.id;
      }
      return '';
    },
    publicProjectId() {
      return this.testCard.project?.projectNumber || '';
    },
    notificationEnabled() {
      return this.testCard.isNotificationsEnabled;
    },
    testInitiated() {
      return this.testCard.status === this.$t('test.card.status.initiated');
    },
    isProjectActive() {
      return Object.keys(ACTIVE_TEST_CARD_TYPES).includes(this.testCard.type);
    },
    canRetry() {
      return [projectStatuses.FAILED, projectStatuses.CANCELED].includes(this.projectStatus) &&
        !!this.projectId.length;
    },
    isDeleteClicked() {
      return this.deleteClicked;
    },
  },
  methods: {
    goToProject() {
      let to = 'projectDetails.tab.technicalInformation';
      if (this.isProjectActive) {
        to = 'projectDetails.tab.automatic.issues';
      }
      this.$router.push({ name: views.PROJECT_DETAILS, params: { id: this.projectId, to } });
    },
    async initiateTest() {
      try {
        await this.$store.dispatch(INITIATE_TEST_CARD, [this.testCard.id]);
      } catch (e) {
        await this.$emit('displayError', e.message);
      }
    },
    async retryScan() {
      if (this.retryInProgress) {
        return;
      }
      this.retryInProgress = true;
      try {
        await this.$store.dispatch(RETRY_SCAN, { projectId: this.projectId });
      } catch (e) {
        await this.$emit('displayError', e.message);
      } finally {
        this.retryInProgress = false;
      }
    },
    deleteTestCard() {
      window.getSelection().removeAllRanges();
      this.deleteClicked = true;
      this.$store.dispatch(DELETE_TEST_CARD, this.testCard.id);
    },
  },
  watch: {
    'testCard.id': {
      handler() {
        this.deleteClicked = false;
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.status {
  text-transform: uppercase;
  border-radius: $space-1;
  padding: $space-0-5 $space-1;
  font-size: $font-size-xs;
  background-color: rgba(155, 176, 190, 0.19);
  color: #9bb0be;

  &.scoping {
    background-color: rgba(249, 193, 46, 0.13);
    color: #f9c12e;
  }

  &.pending {
    background-color: rgba(255, 127, 0, 0.17);
    color: var(--severity-high);
  }

  &.canceled {
    background-color: rgba(155, 176, 190, 0.16);
    color: #9bb0be;
  }

  &.in-progress {
    background-color: rgba(43, 175, 105, 0.18);
    color: var(--severity-low);
  }

  &.finished {
    background-color: rgba(36, 89, 213, 0.16);
    color: var(--severity-medium);
  }

  &.failed {
    background-color: rgba(224, 37, 94, 0.13);
    color: var(--severity-critical);
  }

  &.repeat-in-progress {
    background-color: rgba(43, 175, 105, 0.18);
    color: var(--severity-low);
  }
}

// Shared base for decorative background watermarks (robot, repeat, etc.).
// Uses a CSS mask instead of background-image so the watermark colour is
// driven by a theme variable and follows light/dark mode automatically —
// the SVG shape only acts as a stencil, its internal fill is irrelevant.
%test-card-watermark {
  position: relative;

  // Decorative watermark logo, right-anchored and vertically centred so the
  // full shape stays visible behind the card content. Tinted with a muted
  // theme colour so it reads as a subtle grey in both light and dark mode.
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--text-muted);
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    opacity: 0.15;
    pointer-events: none;
  }
}

.automated-test-card {
  @extend %test-card-watermark;

  &::before {
    mask-image: url('../../assets/icons/robot.svg');
    -webkit-mask-image: url('../../assets/icons/robot.svg');
    mask-position: calc(100% + 18px);
    -webkit-mask-position: calc(100% + 18px);
    mask-size: rem(170);
    -webkit-mask-size: rem(170);
  }
}

.repetitive-test-card {
  @extend %test-card-watermark;

  &::before {
    mask-image: url('../../assets/icons/repeat.svg');
    -webkit-mask-image: url('../../assets/icons/repeat.svg');
    mask-position: calc(100% - 8px);
    -webkit-mask-position: calc(100% - 8px);
    mask-size: rem(130);
    -webkit-mask-size: rem(130);
  }
}

.delete-hover {
  background-color: $widget-no-delta-background-color;
  opacity: 0.2;
  background-size: $space-2 $space-2;
  background-image: repeating-linear-gradient(45deg, $widget-no-delta-text-color 0, $message-color 1px, #e5e5f7 0, #e5e5f7 50%);
  background-repeat: repeat;
  pointer-events: none;
}

.test-card {
  position: relative;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: $space-1;
  box-shadow: 0 0 $space-2 0 rgba(26, 30, 34, 0.08);
  margin-bottom: $space-2;
  margin-right: $space-2;
  width: rem(243);
  height: rem(150);
  padding: $space-3 $space-5;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .premium-feature {
    width: rem(18);
    height: rem(18);
    user-select: none;
    margin-left: $space-2;

    .st0 { fill: #24BEB5; }
    .st1 { fill: #1DA9A0; }
    .st2 { fill: #179291; }
    .st3 { fill: #116B82; }
  }

  .test-name {
    color: var(--text-primary);
    font-family: $font-family-medium;
    font-size: $font-size-base;
    font-weight: 500;
    letter-spacing: 0;
    line-height: $font-size-md;
    margin-bottom: $space-1;
    display: flex;
    flex-direction: row;
  }

  .section-wrapper {
    display: flex;
    flex-direction: row;

    .auto-remediation {
      display: flex;
      flex-direction: column-reverse;
      justify-content: space-between;
      align-items: center;

      .status-with-id {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: $space-0-5;
      }

      .card-project-id {
        font-family: $font-family-regular;
        font-size: xx-small;
        color: var(--text-muted);
        white-space: nowrap;
      }

      &__label {
        border-radius: $space-0-5;
        background-color: $severity-low-text-color;
        min-width: rem(100);
        max-width: rem(100);
        max-height: rem(13);
        text-align: center;
        font-family: $font-family-semibold;
        text-transform: uppercase;
        font-size: xx-small;
        color: var(--bg-primary);
        margin-bottom: $space-1;
        margin-right: rem(-4);
      }
    }
  }

  .test-url-container {
    display: flex;
    align-items: center;
    gap: $space-1-5;
    flex: 1;
    min-width: 0;
  }

  .test-url {
    color: var(--text-muted);
    font-family: $font-family-medium;
    font-size: $font-size-sm;
    // line-height must be >= font-size or the glyphs get clipped at the bottom
    line-height: $font-size-md;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    min-width: 0;
    max-width: rem(110);
  }

  .authentication-key-inline {
    display: flex;
    align-items: center;
    gap: $space-0-5;
    color: var(--text-muted);
    flex-shrink: 0;

    .key-icon {
      font-size: $font-size-sm;
      // CloudServicesAssetRow global rule overrides this — force muted color
      color: var(--text-muted) !important;
    }

    .key-name {
      font-family: $font-family-regular;
      font-size: $font-size-xs;
      color: var(--text-muted);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: rem(80);
    }
  }

  .test-time {
    display: flex;
    flex-direction: column;
    color: var(--text-muted);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    line-height: $font-size-base;
    flex: 1;
    margin-bottom: $space-1;

    &__date-row {
      display: flex;
      justify-content: space-between;
    }
  }

  .test-control {
    align-items: center;
    display: flex;
    justify-content: space-between;

    &__icons {
      align-items: center;
      display: flex;
      gap: $space-0-5;
    }

    .alerts {
      align-items: center;
      background-color: var(--bg-tertiary);
      border-radius: $space-0-5;
      cursor: pointer;
      display: flex;
      height: rem(28);
      justify-content: center;
      width: rem(28);

      &.notificationEnabled {
        background-color: #fef5dd;
      }
    }

    .open-project {
      align-items: center;
      background-color: $action-button-background-color;
      border-radius: $space-0-5;
      cursor: pointer;
      display: flex;
      height: rem(28);
      justify-content: center;
      width: rem(28);

      .v-icon {
        font-size: rem(18);
        // Global .v-icon overrides win over scoped styles — force brand color
        color: var(--brand-secondary) !important;
      }
    }

    .initiate-test-btn {
      background-color: var(--brand-primary) !important;
      border-radius: $space-0-5;
      box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 3px 0 rgba(0, 0, 0, 0.2);
      color: var(--text-inverse) !important;
      font-family: $font-family-medium;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.1px;
      line-height: $font-size-md;
      text-align: center;
      text-transform: uppercase !important;
    }

    .test-initiated {
      color: var(--brand-primary);
      font-family: $font-family-medium;
      font-size: $font-size-sm;
      font-weight: 500;
      text-transform: uppercase;
    }

    .retry-scan {
      align-items: center;
      background-color: var(--brand-primary-bg);
      border-radius: $space-0-5;
      cursor: pointer;
      display: flex;
      height: rem(28);
      justify-content: center;
      width: rem(28);

      .v-icon {
        font-size: rem(18);
        // Global .v-icon overrides win over scoped styles — force brand color
        color: var(--brand-primary) !important;
      }

      .v-progress-circular {
        color: var(--brand-primary);
      }

      &--loading {
        cursor: default;
      }
    }
  }
}

#app .test-card .remove-test {
  position: absolute;
  right: rem(9);
  top: rem(9);

  .v-btn {
    display: block;
    background: #fce9ef;
    width: rem(16);
    height: rem(16);
    border-radius: 50%;
  }

  .v-icon {
    color: #dd475e;
    width: rem(8);
    height: rem(8);
    font-size: rem(13);
    font-weight: bold;
  }

  .automated-remediation {
    border-radius: 50%;
    width: rem(25);
    height: rem(25);
    text-align: center;
    color: var(--text-inverse);
    background-color: $technology-color;
    opacity: 0.5;
    font-family: $font-family-medium;
    font-size: $font-size-sm;
    font-weight: 500;
    padding-top: $space-0-5;

    .robot-icon {
      opacity: 1;
      fill: white;
      width: rem(18);
      height: rem(18);
    }
  }
}
</style>
